import { $, $Element } from '../utils/element';
import { ScreenSize } from '../utils/screen-size';

interface IPointerOptions {
    isTop: boolean;
    leftShift: number;
    sizeX: number;
    sizeY: number;
}

interface ISize {
    width: number;
    height: number;
}

export interface IPosition {
    left: number;
    top: number;
}

interface INodeMargins {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

interface IPositionSide {
    rightleft: string;
    bottomtop: string;
}

interface IBoundingRect {
    left: number;
    right: number;
    top: number;
    bottom: number;
    hMiddle: number;
    vMiddle: number;
    width: number;
    height: number;
}

export class PositionAlignment {
    private static positionSide: IPositionSide = {
        rightleft: 'rightleft',
        bottomtop: 'bottomtop'
    };

    private static get margin(): number {
        return ScreenSize.getWindowWidth() < 960 ? 0 : 20;
    }

    private static appendPointer(nodeElement: $Element, options: IPointerOptions, pointerColorFrom: string) {
        const pointerElement = $(document.createElement('span'));
        pointerElement.addClass('pointer js-pointer');
        pointerElement.innerHTML(`<span class="pointer__inside"></span>`);

        this.setPositionOfPointer(pointerElement, options);
        // const backgroundElement = nodeElement.hasClass('js-component-content')
        //     ? nodeElement
        //     : nodeElement.find('.js-component-content');

        // pointerElement.find('.pointer__inside').setCss({
        //     backgroundColor: backgroundElement.getCss(pointerColorFrom)
        // });

        nodeElement.append(pointerElement.element);

    }

    private static setPositionOfPointer(pointerElement: $Element, options: IPointerOptions) {
        const borderSize = 1;
        const left = options.leftShift ? `calc(50% - ${options.leftShift + options.sizeX + borderSize}px)` : `calc(50% - ${options.sizeX + borderSize}px)`;
        if (options.isTop) {
            pointerElement.setCss({
                left: left,
                top: `${-options.sizeY}px`
            });
            pointerElement.removeClass('pointer--bottom');
            pointerElement.addClass('pointer--top');

        } else {
            pointerElement.setCss({
                left: left,
                top: '100%'
            });
            pointerElement.removeClass('pointer--top');
            pointerElement.addClass('pointer--bottom');
        }
    }

    private static getElementBoundingRect(element: $Element): IBoundingRect {
        const offset = element.offset();
        return {
            left: offset.left,
            right: offset.left + offset.width,
            top: offset.top,
            bottom: offset.top + offset.height,
            hMiddle: offset.top + offset.height / 2,
            vMiddle: offset.left + offset.width / 2,
            width: offset.width,
            height: offset.height
        };
    }

    private static getNodeMargins(nodeSizes: ISize, nodePosition: IPosition): INodeMargins {
        const margins = {
            right: ScreenSize.getWindowWidth() - (nodePosition.left + nodeSizes.width),
            bottom: ScreenSize.getWindowHeight() - (nodePosition.top + nodeSizes.height),

            left: nodePosition.left,
            top: nodePosition.top,
        };

        const margin = this.margin;

        //uwzględnienie odstepu od krawedzi ekranu
        margins.right =
            margins.right <= 0 ? (margins.right -= margin) //mniejsze niz 0
                :
                margins.right > 0 && margins.right < margin ? (-margin) // wieksze niz 0 ale mniejsze niz margin
                    :
                    (margins.right); // default

        margins.left =
            margins.left <= 0 ? (margins.left -= margin) //mniejsze niz 0
                :
                margins.left > 0 && margins.left < margin ? (margin) // wieksze niz 0 ale mniejsze niz margin
                    :
                    (margins.left); // default

        return margins;
    }

    public static setPositionByNode(nodeElement: $Element, anchor: $Element, side: keyof IPositionSide, itemHeight = 0): void {
        const baseNodeMargin = 10;

        if (nodeElement && anchor) {
            if (side === 'rightleft') {
                const anchorOffset = anchor.offset();
                const nodeOffset = nodeElement.offset();
                const anchorPositionCalcuclated = {
                    left: anchorOffset.left + anchorOffset.width,
                    top: anchorOffset.top
                };
                const nodeMargins = this.getNodeMargins(nodeOffset, anchorPositionCalcuclated);

                if (nodeMargins.right < 0) {
                    if ((nodeMargins.left - anchorOffset.width) < nodeOffset.width) {
                        anchorPositionCalcuclated.left = ScreenSize.getWindowWidth() - nodeOffset.width - baseNodeMargin;
                        anchorPositionCalcuclated.top = anchorPositionCalcuclated.top + (itemHeight / 2);
                    } else {
                        anchorPositionCalcuclated.left = anchorOffset.left - nodeOffset.width;
                    }
                }

                if (nodeMargins.bottom < itemHeight) {
                    anchorPositionCalcuclated.top = ScreenSize.getWindowHeight() - nodeOffset.height - baseNodeMargin;
                }

                anchorPositionCalcuclated.left = Math.max(baseNodeMargin, anchorPositionCalcuclated.left);
                anchorPositionCalcuclated.top = Math.max(baseNodeMargin, anchorPositionCalcuclated.top);

                nodeElement.setCss({
                    left: `${anchorPositionCalcuclated.left}px`,
                    top: `${anchorPositionCalcuclated.top}px`
                });
            }

            if (side === 'bottomtop') {
                const anchorOffset = anchor.offset();
                const nodeOffset = nodeElement.offset();
                const anchorPositionCalcuclated = {
                    left: anchorOffset.left,
                    top: anchorOffset.top + anchorOffset.height
                };
                const nodeMargins = this.getNodeMargins(nodeOffset, anchorPositionCalcuclated);

                if (nodeMargins.bottom < 0) {
                    if (nodeMargins.top < 0) {
                        anchorPositionCalcuclated.top = 0;
                    } else {
                        anchorPositionCalcuclated.top = anchorOffset.top - nodeOffset.height;
                    }
                }

                if (nodeMargins.right < 0) {
                    anchorPositionCalcuclated.left = anchorOffset.left - (nodeOffset.width - anchorOffset.width);
                }

                nodeElement.setCss({
                    left: `${anchorPositionCalcuclated.left}px`,
                    top: `${anchorPositionCalcuclated.top}px`
                });
            }
        }
    }

    public static setPositionByNodeWithPointer(nodeElement: $Element, anchor: $Element, pointerColorFrom = 'backgroundColor'): void {
        if (nodeElement && anchor) {
            const anchorBoundingRect = this.getElementBoundingRect(anchor);
            const nodeOffset = nodeElement.offset();
            const pointerOptions = {
                isTop: true,
                leftShift: 0,
                sizeX: 22,
                sizeY: 11
            };

            const nodePositionLeft = anchorBoundingRect.vMiddle - nodeOffset.width / 2;

            const nodeBottomPosition = {// bottom position
                left: nodePositionLeft,
                top: anchorBoundingRect.bottom + pointerOptions.sizeY
            };
            const nodeBottomMargins = this.getNodeMargins(nodeOffset, nodeBottomPosition);

            const nodeTopPosition = { // top position
                left: nodePositionLeft,
                top: anchorBoundingRect.top - nodeOffset.height - pointerOptions.sizeY
            };
            const nodeTopMargins = this.getNodeMargins(nodeOffset, nodeTopPosition);

            let nodeNewPosition = {
                left: nodePositionLeft,
                top: 0
            };

            if (nodeBottomMargins.bottom >= 0) {
                nodeNewPosition = nodeBottomPosition;
            } else if (nodeTopMargins.top >= 0) {
                nodeNewPosition = nodeTopPosition;
                pointerOptions.isTop = false;
            }

            if (nodeBottomMargins.right < 0) {
                const shift = nodeBottomMargins.right;
                nodeNewPosition.left += shift;
                pointerOptions.leftShift = shift;
            } else if (nodeBottomMargins.left < 0) {
                const shift = nodeBottomMargins.left;
                nodeNewPosition.left += (shift * -1);
                pointerOptions.leftShift = (shift * -1);
            }

            const pointerElement = nodeElement.find('.js-pointer');
            if (pointerElement) {
                this.setPositionOfPointer(pointerElement, pointerOptions);
            } else {
                this.appendPointer(nodeElement, pointerOptions, pointerColorFrom);
            }

            nodeElement.setCss({
                left: `${nodeNewPosition.left}px`,
                top: `${nodeNewPosition.top}px`
            });
        }
    }

    public static setPositionByAnchor(nodeElement: $Element, anchor: IPosition): void {
        if (nodeElement.element) {
            const nodeHeight = nodeElement.element.offsetHeight;
            const nodeWidth = nodeElement.element.offsetWidth;
            if (anchor.left + nodeWidth > ScreenSize.getWindowWidth()) {
                anchor.left = anchor.left - nodeWidth;
            }
            if (anchor.top + nodeHeight > ScreenSize.getWindowHeight()) {
                anchor.top = anchor.top - nodeHeight;
                if (anchor.top < 0) {
                    anchor.top = 0;
                }
            }

            nodeElement.setCss({
                left: `${anchor.left}px`,
                top: `${anchor.top}px`
            });
        }
    }
}
